import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Landmark, Lock, CheckCircle2, FileDown, Mail, CalendarClock, RotateCcw, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function AngebotPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(null);
  const [altLiefer, setAltLiefer] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingStripe, setLoadingStripe] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/angebot/${token}`);
      setData(r.data);
      if (!form) {
        const k = r.data.kunde;
        setForm({ ...k, rechnungsadresse: { strasse: "", plz: "", ort: "", land: "DE", adresszusatz: "", ...(k.rechnungsadresse || {}) }, lieferadresse: k.lieferadresse || null });
        setAltLiefer(!!k.lieferadresse);
      }
    } catch (e) { setErr(e?.response?.data?.detail || "Angebot nicht gefunden."); }
  };
  useEffect(() => { load();  }, [token]);

  if (err) return <ErrorPage msg={err} />;
  if (!data || !form) return <div className="min-h-screen flex items-center justify-center text-slate-500">Lade…</div>;

  const confirmed = data.address_confirmed;
  const amt = data.angebot;
  if (data.storno) return <StornoPage data={data} token={token} onReactivated={load} />;

  const setK = (k, v) => setForm({ ...form, [k]: v });
  const setR = (k, v) => setForm({ ...form, rechnungsadresse: { ...form.rechnungsadresse, [k]: v } });
  const setL = (k, v) => setForm({ ...form, lieferadresse: { ...(form.lieferadresse || { land: "DE" }), [k]: v } });

  const submit = async () => {
    if (!form.rechnungsadresse.strasse || !form.rechnungsadresse.plz || !form.rechnungsadresse.ort || !form.email) {
      toast.error("Bitte alle Pflichtfelder (Straße, PLZ, Ort, E-Mail) ausfüllen."); return;
    }
    if (!/^\d{4,5}$/.test(form.rechnungsadresse.plz)) { toast.error("PLZ-Format ungültig."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/angebot/${token}/adresse`, { ...form, lieferadresse: altLiefer ? form.lieferadresse : null });
      toast.success(data.kostenlos ? "Adresse bestätigt – vielen Dank! Wir bereiten Ihre Lieferung vor." : "Adresse bestätigt. Zahlungsoptionen sind jetzt verfügbar.");
      await load();
      window.scrollTo({ top: 9999, behavior: "smooth" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setSubmitting(false); }
  };

  const payStripe = async () => {
    setLoadingStripe(true);
    try {
      const r = await axios.post(`${API}/public/angebot/${token}/stripe-session`);
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Stripe-Fehler."); setLoadingStripe(false); }
  };

  const toggleBank = async () => {
    const next = !showBank;
    setShowBank(next);
    if (next) { try { await axios.post(`${API}/public/angebot/${token}/bank-details-viewed`); } catch {} }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-4">
          {data.firma.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${data.firma.logo_url}`} alt="Logo" className="h-10 w-auto" />}
          <div className="flex-1">
            <div className="font-bold text-lg text-slate-900">{data.firma.firma_name || "Die Druckexperten"}</div>
            <div className="text-xs text-slate-500">{data.firma.strasse} · {data.firma.plz} {data.firma.ort}</div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div className="font-mono">{amt.auftragsnummer}</div>
            <div>Angebot {amt.angebots_datum} · gültig bis {amt.gueltig_bis}</div>
          </div>
        </div>
      </header>
      {amt.zahlungsfrist_datum && !data.payment?.paid && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="portal-zahlungsfrist">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-2 text-sm text-amber-900"><CalendarClock className="w-4 h-4" /> Bitte bezahlen Sie bis <b>{amt.zahlungsfrist_datum}</b>.</div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h1 className="text-2xl font-bold text-slate-900 mb-4">{amt.titel}</h1>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">Pos.</th><th className="py-2">Beschreibung</th><th className="py-2 text-right">Menge</th><th className="py-2 text-right">Einzel</th><th className="py-2 text-right">Gesamt</th></tr></thead>
              <tbody>
                {amt.positionen.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2"><div className="font-medium whitespace-pre-line">{p.name || p.beschreibung}</div><div className="text-xs text-slate-500 whitespace-pre-line">{p.name ? p.beschreibung : [p.material, p.farbe].filter(Boolean).join(" · ")}</div>
                      {p.rabatt_prozent > 0 && <div className="text-xs text-amber-700" data-testid={`portal-pos-rabatt-${i}`}>Rabatt {Number(p.rabatt_prozent).toLocaleString("de-DE")} %</div>}</td>
                    <td className="py-2 text-right">{p.menge}</td>
                    <td className="py-2 text-right font-mono">{fmtEUR(p.einzelpreis_netto)}</td>
                    <td className="py-2 text-right font-mono">{fmtEUR(amt.summen?.rabatt_aktiv ? amt.summen.zeilen[i]?.netto : p.einzelpreis_netto * p.menge)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 ml-auto w-72 text-sm font-mono">
              {amt.summen?.gesamtrabatt_betrag > 0 && <div className="flex justify-between text-amber-700" data-testid="portal-gesamtrabatt"><span>{amt.summen.gesamtrabatt_text}:</span><span>−{fmtEUR(amt.summen.gesamtrabatt_betrag)}</span></div>}
              <div className="flex justify-between"><span>Netto:</span><span>{fmtEUR(amt.summe_netto)}</span></div>
              <div className="flex justify-between"><span>MwSt 19 %:</span><span>{fmtEUR(amt.summe_mwst)}</span></div>
              <div className="flex justify-between border-t-2 border-slate-900 pt-1 mt-1 font-semibold text-base"><span>Brutto:</span><span data-testid="portal-brutto">{fmtEUR(amt.summe_brutto)}</span></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => window.open(`${API}/public/angebot/${token}/pdf`, "_blank")} data-testid="portal-pdf-button">
                <FileDown className="w-4 h-4 mr-2" /> Angebot als PDF
              </Button>
              {amt.rechnung_verfuegbar && (
                <Button variant="outline" onClick={() => window.open(`${API}/public/angebot/${token}/rechnung/pdf`, "_blank")} data-testid="portal-rechnung-pdf-button">
                  <FileDown className="w-4 h-4 mr-2" /> Rechnung{amt.rechnungsnummer ? ` ${amt.rechnungsnummer}` : ""} als PDF
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {data.kostenlos && <KostenlosCard data={data} />}

        {(!data.kostenlos || data.adresse_noetig) && (
        <Card className={`border-2 ${confirmed ? "border-emerald-200" : "border-amber-300 bg-amber-50/50"}`} data-testid="portal-address-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              {confirmed ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Lock className="w-6 h-6 text-amber-700" />}
              <h2 className="text-lg font-semibold">{confirmed ? "Adresse bestätigt" : data.kostenlos ? "Bitte Adresse bestätigen" : "Bitte Rechnungsadresse bestätigen"}</h2>
            </div>
            {!confirmed && <p className="text-sm text-amber-900">{data.kostenlos ? "Damit wir Ihre kostenlose Lieferung versenden können, bestätigen Sie bitte Ihre Adresse." : "Bestätigen Sie Ihre Rechnungsadresse, um die Zahlungsoptionen anzuzeigen."}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Anrede</Label><Input value={form.anrede} onChange={(e) => setK("anrede", e.target.value)} disabled={confirmed} data-testid="portal-anrede" /></div>
              <div><Label>Titel</Label><Input value={form.titel} onChange={(e) => setK("titel", e.target.value)} disabled={confirmed} /></div>
              <div><Label>Vorname</Label><Input value={form.vorname} onChange={(e) => setK("vorname", e.target.value)} disabled={confirmed} /></div>
              <div><Label>Nachname</Label><Input value={form.nachname} onChange={(e) => setK("nachname", e.target.value)} disabled={confirmed} /></div>
              <div className="col-span-2"><Label>Firma (optional)</Label><Input value={form.firma} onChange={(e) => setK("firma", e.target.value)} disabled={confirmed} /></div>
              <div><Label>USt-IdNr. (optional)</Label><Input value={form.ust_id} onChange={(e) => setK("ust_id", e.target.value)} disabled={confirmed} /></div>
              <div><Label>Telefon</Label><Input value={form.telefon} onChange={(e) => setK("telefon", e.target.value)} disabled={confirmed} /></div>
              <div className="col-span-2"><Label>E-Mail *</Label><Input type="email" value={form.email} onChange={(e) => setK("email", e.target.value)} disabled={confirmed} data-testid="portal-email" /></div>
              <div className="col-span-2"><Label>Straße + Hausnr. *</Label><Input value={form.rechnungsadresse.strasse} onChange={(e) => setR("strasse", e.target.value)} disabled={confirmed} data-testid="portal-strasse" /></div>
              <div className="col-span-2"><Label>Adresszusatz</Label><Input value={form.rechnungsadresse.adresszusatz || ""} onChange={(e) => setR("adresszusatz", e.target.value)} disabled={confirmed} /></div>
              <div><Label>PLZ *</Label><Input value={form.rechnungsadresse.plz} onChange={(e) => setR("plz", e.target.value)} disabled={confirmed} data-testid="portal-plz" /></div>
              <div><Label>Ort *</Label><Input value={form.rechnungsadresse.ort} onChange={(e) => setR("ort", e.target.value)} disabled={confirmed} data-testid="portal-ort" /></div>
              <div><Label>Land</Label><Input value={form.rechnungsadresse.land} onChange={(e) => setR("land", e.target.value)} disabled={confirmed} /></div>
            </div>
            {!confirmed && (
              <>
                <div className="flex items-center gap-3 pt-2">
                  <Switch checked={altLiefer} onCheckedChange={setAltLiefer} data-testid="portal-alt-liefer-switch" />
                  <span className="text-sm">Lieferadresse weicht ab</span>
                </div>
                {altLiefer && (
                  <div className="grid grid-cols-2 gap-3 bg-white p-3 rounded border">
                    <div className="col-span-2"><Label>Lieferadresse — Straße + Nr.</Label><Input onChange={(e) => setL("strasse", e.target.value)} /></div>
                    <div><Label>PLZ</Label><Input onChange={(e) => setL("plz", e.target.value)} /></div>
                    <div><Label>Ort</Label><Input onChange={(e) => setL("ort", e.target.value)} /></div>
                  </div>
                )}
                <Button onClick={submit} disabled={submitting} className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700" data-testid="portal-confirm-address">
                  {data.kostenlos ? "Adresse bestätigen" : "Adresse bestätigen & zu den Zahlungsoptionen →"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {!data.kostenlos && confirmed && data.payment && !data.payment.paid && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-payment-options">
            <Card className="border-blue-200 border-2">
              <CardContent className="p-6 space-y-3">
                <CreditCard className="w-8 h-8 text-blue-600" />
                <h3 className="font-semibold text-lg">Kartenzahlung</h3>
                <p className="text-sm text-slate-600">{data.paypal_aktiv ? "Schnell & sicher per Visa, Mastercard, PayPal, Apple Pay oder Google Pay." : "Schnell & sicher per Visa, Mastercard, Apple Pay oder Google Pay."}</p>
                <Button onClick={payStripe} disabled={loadingStripe || !data.payment.stripe_available} className="w-full h-11 bg-blue-600 hover:bg-blue-700" data-testid="portal-pay-stripe">
                  Jetzt {fmtEUR(data.payment.amount_brutto)} zahlen →
                </Button>
                {!data.payment.stripe_available && <p className="text-xs text-rose-600">Kartenzahlung aktuell nicht verfügbar.</p>}
              </CardContent>
            </Card>
            <BankCard data={data} showBank={showBank} toggleBank={toggleBank} token={token} />
          </div>
        )}

        {data.payment?.paid && (
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
              <h2 className="text-xl font-bold mt-2">Zahlung erhalten — vielen Dank!</h2>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

// 0-€-Angebot (Goodie/Muster): keine Zahlungsarten, nur Hinweis + ggf. Adressbestätigung
function KostenlosCard({ data }) {
  const offen = data.adresse_noetig;
  return (
    <Card className="border-2 border-emerald-200 bg-emerald-50" data-testid="portal-kostenlos-card">
      <CardContent className="p-6 flex items-start gap-4">
        <Gift className="w-10 h-10 text-emerald-600 shrink-0" />
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-emerald-900" data-testid="portal-kostenlos-hinweis">{data.kostenlos_hinweis}</h2>
          <p className="text-sm text-emerald-800" data-testid="portal-kostenlos-status">
            {offen ? "Es ist keine Zahlung nötig. Bitte bestätigen Sie nur noch Ihre Adresse." : "Es ist keine Zahlung nötig – wir bereiten Ihre Lieferung vor."}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function BankCard({ data, showBank, toggleBank, token }) {
  const [sending, setSending] = useState(false);
  const pct = data.skonto_prozent || 0;
  const original = data.summe_brutto_original;
  const rabatt = data.summe_brutto_mit_skonto;
  const bank = data.bank_data || {};
  const sendMail = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/public/angebot/${token}/zahldaten-per-mail`);
      toast.success(r.data.sent ? `Zahlungsdaten wurden an ${r.data.to} gesendet.` : `Zahlungsdaten werden in Kürze an ${r.data.to} gesendet.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen."); }
    finally { setSending(false); }
  };
  return (
    <Card className={`border-2 ${pct > 0 ? "border-emerald-300" : "border-slate-200"} ${showBank ? "md:col-span-2" : ""}`} data-testid="portal-bank-card">
      <CardContent className="p-6 space-y-3">
        <Landmark className="w-8 h-8 text-slate-700" />
        <h3 className="font-semibold text-lg">Überweisung</h3>
        {data.skonto_werbetext && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-3 text-sm font-medium" data-testid="portal-skonto-hint">{data.skonto_werbetext}</div>
        )}
        {pct > 0 ? (
          <div className="flex items-baseline gap-3" data-testid="portal-skonto-price">
            <span className="text-slate-400 line-through text-lg font-mono" data-testid="portal-price-original">{fmtEUR(original)}</span>
            <span className="text-emerald-700 text-3xl font-bold font-mono" data-testid="portal-price-skonto">nur {fmtEUR(rabatt)}</span>
          </div>
        ) : (
          <p className="text-sm text-slate-600">Zahlen Sie bequem per Banküberweisung. Bitte Verwendungszweck angeben.</p>
        )}
        {!showBank ? (
          <Button variant="outline" onClick={toggleBank} className="w-full h-11" data-testid="portal-show-bank">Zahlungsdaten für Überweisung anzeigen</Button>
        ) : (
          <div className="bg-slate-50 border rounded-md p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6" data-testid="portal-bank-panel">
            <div className="space-y-2 text-sm">
              <div className="text-emerald-700 text-2xl font-bold font-mono" data-testid="portal-bank-amount">Zu zahlen: {fmtEUR(rabatt)}</div>
              <div><span className="text-slate-500">Kontoinhaber:</span> <b>{bank.kontoinhaber}</b></div>
              <div><span className="text-slate-500">IBAN:</span> <b className="font-mono">{bank.iban || "—"}</b></div>
              {bank.bic && <div><span className="text-slate-500">BIC:</span> <b className="font-mono">{bank.bic}</b></div>}
              {bank.bank && <div><span className="text-slate-500">Bank:</span> <b>{bank.bank}</b></div>}
              <div><span className="text-slate-500">Verwendungszweck:</span> <b className="font-mono text-rose-700" data-testid="portal-verwendungszweck">{data.verwendungszweck}</b></div>
              <p className="text-xs text-slate-600 pt-1">Bitte den Verwendungszweck unbedingt angeben, damit wir Ihre Zahlung zuordnen können.</p>
              <Button onClick={sendMail} disabled={sending} variant="outline" className="mt-2" data-testid="portal-send-zahldaten">
                <Mail className="w-4 h-4 mr-2" /> {sending ? "Wird gesendet…" : "Zahldaten per Mail senden"}
              </Button>
            </div>
            {data.girocode_aktiv && (
              data.girocode_svg ? (
                <div className="flex flex-col items-center gap-1" data-testid="portal-girocode">
                  <div className="w-[240px] h-[240px] bg-white p-2 rounded border [&_svg]:w-full [&_svg]:h-full" dangerouslySetInnerHTML={{ __html: data.girocode_svg }} />
                  <span className="text-xs text-slate-500">GiroCode mit Banking-App scannen</span>
                </div>
              ) : (
                <div className="text-xs text-slate-500 max-w-[240px] self-center" data-testid="portal-girocode-missing">GiroCode nicht verfügbar — bitte Bankdaten in Firmendaten eintragen.</div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ErrorPage({ msg }) {
  return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
    <Card className="max-w-md w-full"><CardContent className="p-8 text-center space-y-3"><Lock className="w-10 h-10 mx-auto text-slate-400" /><h1 className="text-xl font-semibold">Angebot nicht verfügbar</h1><p className="text-sm text-slate-600">{msg}</p></CardContent></Card>
  </div>;
}

function StornoPage({ data, token, onReactivated }) {
  const [busy, setBusy] = useState(false);
  const s = data.storno;
  const f = data.firma;
  const reactivate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/angebot/${token}/reaktivieren`);
      toast.success(`Angebot reaktiviert – bitte bezahlen Sie bis ${r.data.zahlungsfrist_datum}.`);
      await onReactivated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reaktivierung fehlgeschlagen."); await onReactivated(); }
    finally { setBusy(false); }
  };
  return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
    <Card className="max-w-lg w-full" data-testid="portal-storno"><CardContent className="p-8 text-center space-y-4">
      <CalendarClock className="w-10 h-10 mx-auto text-slate-400" />
      <h1 className="text-xl font-semibold">Angebot {data.angebot.auftragsnummer}</h1>
      {s.reaktivierbar ? (<>
        <p className="text-sm text-slate-600">Die Zahlungsfrist ist abgelaufen und das Angebot wurde storniert. Sie können es bis <b>{s.reaktivierbar_bis_datum}</b> zu den gleichen Konditionen reaktivieren und direkt bezahlen.</p>
        <Button onClick={reactivate} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="portal-reactivate-button"><RotateCcw className="w-4 h-4 mr-2" /> Angebot reaktivieren und bezahlen</Button>
      </>) : (<>
        <p className="text-sm text-slate-700" data-testid="portal-expired">Dieses Angebot ist abgelaufen. Bitte kontaktieren Sie uns.</p>
        <p className="text-sm text-slate-600" data-testid="portal-contact">{f.firma_name}{f.email && <><br />E-Mail: <a className="text-blue-700" href={`mailto:${f.email}`}>{f.email}</a></>}{f.telefon && <><br />Telefon: {f.telefon}</>}</p>
      </>)}
    </CardContent></Card>
  </div>;
}
